import clsx from 'clsx';
import { last } from 'lodash';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';

import { Breadcrumb, DashboardContext, HeroBanner, Icon, Skeleton, TabPanel, Tabs, TabsProvider } from 'components';
import { routes } from 'router';

import dashboardHero from 'assets/img/dashboard-hero.jpg';
import 'assets/styles/components/pages/dashboard-page.scss';

const DashboardPage = () => {
  const location = useLocation();

  const { tabs, user, userLoading } = useContext(DashboardContext);

  const { t } = useTranslation();

  const getTabIndex = () => {
    const tabPath = location.pathname.split('/').pop();
    const index = tabs.findIndex(({ path }) => path === tabPath);
    return index === -1 ? 0 : index;
  };

  const breadcrumbs = useMemo(() => {
    const tabIndex = getTabIndex();

    return {
      isAccount: tabIndex === 0,
      items: [
        { label: t('breadcrumb.home'), url: routes.home.path },
        { label: t('breadcrumb.dashboard'), url: routes.dashboard.children.account.path },
        { label: tabIndex !== 0 ? tabs[tabIndex]?.label : null }
      ]
    };
  }, [location.pathname]);

  const defaultTab = useMemo(() => {
    if (location.pathname.includes(routes.dashboard.children.orders.path)) {
      return 'orders';
    } else {
      return tabs[getTabIndex()]?.identifier;
    }
  }, [location.pathname]);

  return (
    <TabsProvider defaultTab={defaultTab}>
      <HeroBanner image={{ frontUrl: dashboardHero }} title={last(breadcrumbs.items)?.label} />

      <div
        className={clsx('dashboard-container xl:px-20 2xl:px-40 3xl:px-72 print:px-2', defaultTab)}
        data-test-id="dashboardContainer"
      >
        {/* Desktop View */}
        <div className="hidden lg:block lg:-mt-40">
          <div className="bg-white rounded-lg shadow-md shadow-gray-500/25 p-8 pb-0 relative z-1">
            {userLoading && (
              <>
                <Skeleton classNames="w-[30%] h-6" />
                <Skeleton classNames="w-[30%] h-6" />
                <Skeleton classNames="w-[30%] h-6" />
              </>
            )}

            {!userLoading && user && (
              <>
                <h1 className="h2 mb-4">{`${user.name} ${user.firstName}`}</h1>
                <p className="text-gray-400 flex gap-2 items-center mb-4">
                  <Icon name="user" iconStyle="fad" classes="fa-swap-opacity" />
                  {user.businessRoleName}
                </p>
                <p className="text-gray-400 flex gap-2 items-center mb-4">
                  <Icon name="location-dot" iconStyle="fad" />
                  {user.businessName}
                </p>
                <p className="text-gray-400 flex gap-2 items-center mb-4">
                  <Icon name="at" />
                  {user.email}
                </p>
              </>
            )}
            <Tabs className="mt-8 pt-2 border-t text-gray-500" tabs={tabs} />
          </div>

          <div className="dashboard-content">
            {tabs.map((tab, i) => (
              <TabPanel identifier={tab.identifier} key={`dashboard-tabs-${i}`}>
                <Outlet />
              </TabPanel>
            ))}
          </div>
        </div>

        {/* Mobile View */}
        <div className="mobile-tabs">
          {!breadcrumbs.isAccount && (
            <div className="mb-4">
              <Breadcrumb breadcrumbItems={breadcrumbs.items} />
            </div>
          )}

          <Outlet />
        </div>
      </div>
    </TabsProvider>
  );
};

export default DashboardPage;
